import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Surface, Btn, PageHeader } from "../components/Primitives";
import Disclaimer from "../components/Disclaimer";
import { ShieldCheck, ArrowRight, Sparkle } from "@phosphor-icons/react";

export default function Airworthiness() {
  const [dashboard, setDashboard] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/ads/dashboard");
    setDashboard(data);
  };

  useEffect(() => { load(); }, []);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post("/ads/recompute", {});
      load();
    } finally {
      setBusy(false);
    }
  };

  const totals = dashboard ? dashboard.aircraft.reduce(
    (acc, r) => ({
      expired:     acc.expired + (r.counts.expired || 0),
      urgent_7d:   acc.urgent_7d + (r.counts.urgent_7d || 0),
      warning_30d: acc.warning_30d + (r.counts.warning_30d || 0),
      total:       acc.total + (r.counts.total || 0),
    }),
    { expired: 0, urgent_7d: 0, warning_30d: 0, total: 0 },
  ) : { expired: 0, urgent_7d: 0, warning_30d: 0, total: 0 };

  return (
    <div className="container py-10 space-y-8" data-testid="airworthiness-page">
      <PageHeader
        overline="Compliance"
        title="Airworthiness dashboard"
        subtitle="Fleet-wide AD / SB compliance heatmap. Click any aircraft for full per-AD compliance."
        actions={(
          <Btn variant="ghost" onClick={recompute} disabled={busy} data-testid="ad-dashboard-recompute">
            <Sparkle size={14} className="inline mr-1.5" />{busy ? "Recomputing…" : "Re-match all"}
          </Btn>
        )}
      />

      <Disclaimer variant="compact" />

      {/* Fleet totals */}
      <Surface className="p-6" data-testid="ad-dashboard-totals">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Fleet totals</div>
            <div className="text-xs text-zinc-500 mt-1">Open ADs across all aircraft, by urgency.</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">EXPIRED</div>
              <div className="text-3xl font-mono text-red-400">{totals.expired}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">URGENT</div>
              <div className="text-3xl font-mono text-orange-400">{totals.urgent_7d}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">30D</div>
              <div className="text-3xl font-mono text-amber">{totals.warning_30d}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Total</div>
              <div className="text-3xl font-mono">{totals.total}</div>
            </div>
          </div>
        </div>
      </Surface>

      {/* Per-aircraft heatmap */}
      <Surface className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono flex items-center gap-2">
            <ShieldCheck size={14} className="text-amber" />Per-aircraft heatmap
          </div>
        </div>
        {!dashboard?.aircraft?.length ? (
          <div className="py-10 text-center text-zinc-500">No aircraft in the fleet yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-800">
                <th className="text-left py-2.5 px-3">Registration</th>
                <th className="text-left py-2.5 px-3">Model</th>
                <th className="text-right py-2.5 px-3">Expired</th>
                <th className="text-right py-2.5 px-3">Urgent</th>
                <th className="text-right py-2.5 px-3">30d</th>
                <th className="text-right py-2.5 px-3">Total ADs</th>
                <th className="text-right py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody data-testid="ad-dashboard-table">
              {dashboard.aircraft.map((row) => (
                <tr key={row.aircraft_id} className="border-b border-zinc-900" data-testid={`ad-dashboard-row-${row.aircraft_id}`}>
                  <td className="py-3 px-3 font-mono text-zinc-200">{row.registration}</td>
                  <td className="py-3 px-3 text-zinc-400">{[row.manufacturer, row.model].filter(Boolean).join(" ") || "—"}</td>
                  <td className={`py-3 px-3 text-right font-mono ${row.counts.expired > 0 ? "text-red-400" : "text-zinc-600"}`}>{row.counts.expired}</td>
                  <td className={`py-3 px-3 text-right font-mono ${row.counts.urgent_7d > 0 ? "text-orange-400" : "text-zinc-600"}`}>{row.counts.urgent_7d}</td>
                  <td className={`py-3 px-3 text-right font-mono ${row.counts.warning_30d > 0 ? "text-amber" : "text-zinc-600"}`}>{row.counts.warning_30d}</td>
                  <td className="py-3 px-3 text-right font-mono">{row.counts.total}</td>
                  <td className="py-3 px-3 text-right">
                    <Link to={`/fleet/${row.aircraft_id}#tab=Airworthiness`} className="text-amber hover:underline text-[10px] uppercase tracking-[0.2em] font-mono" data-testid={`ad-dashboard-open-${row.aircraft_id}`}>
                      Open <ArrowRight size={11} className="inline ml-1" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
